import React from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Divider,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { ArrowBack, CheckCircleOutline } from "@mui/icons-material";
import { fToNow } from "../../../utils/formatTime";
import OrderItemsList from "./OrderItemsList";
import {
    getOrderTitle,
    getItemsQtyTotal,
    getOrderCreatedAt,
    getWaiterLabel,
} from "../utils";

export default function KitchenOrderDetailsCard({
    t,
    selectedOrder,
    selectedItems,
    selectedLate,
    distanceLocale,
    usersByName,
    showItemsOnKds,
    onAdvanceItem,
    isItemDisabled,
    getNextStatus,
    onClose,
    onMarkReady,
    isMarkReadyLoading,
}) {
    return (
        <Card
            elevation={0}
            sx={{
                border: "2px solid",
                borderColor: selectedLate ? "error.main" : "primary.main",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: selectedLate ? "error.50" : undefined,
            }}
        >
            <CardContent
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                }}
            >
                <Stack spacing={1.25}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1,
                        }}
                    >
                        <Box
                            sx={{
                                minWidth: 0,
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                            }}
                        >
                            <IconButton
                                onClick={onClose}
                                sx={{ mt: 0.25 }}
                                aria-label={t("kds.backToOrders")}
                            >
                                <ArrowBack />
                            </IconButton>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 900,
                                        lineHeight: 1.1,
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {getOrderTitle(
                                        selectedOrder,
                                        t("kds.orderFallback")
                                    )}
                                </Typography>
                                {selectedOrder?.table_description && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {selectedOrder.table_description}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: "block",
                                    }}
                                >
                                    {selectedOrder?.name}
                                </Typography>
                            </Box>
                        </Box>
                        <Chip
                            size="small"
                            color={selectedLate ? "error" : "warning"}
                            label={
                                selectedLate ? t("kds.late") : t("kds.pending")
                            }
                        />
                    </Box>

                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        <Chip
                            size="small"
                            label={t("kds.itemsCount", {
                                count: getItemsQtyTotal(
                                    selectedItems,
                                    selectedOrder?.entry_items
                                ),
                            })}
                        />
                        {selectedOrder?.owner ? (
                            <Chip
                                size="small"
                                label={getWaiterLabel(
                                    t,
                                    selectedOrder.owner,
                                    usersByName
                                )}
                            />
                        ) : null}
                        {(() => {
                            const createdAt = getOrderCreatedAt(selectedOrder);
                            return createdAt ? (
                                <Chip
                                    size="small"
                                    label={t("kds.createdAgo", {
                                        time: fToNow(createdAt, distanceLocale),
                                    })}
                                />
                            ) : null;
                        })()}
                    </Stack>

                    <Divider />

                    <Box>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 900,
                                mb: 1,
                            }}
                        >
                            {t("kds.entries")}
                        </Typography>
                        <OrderItemsList
                            items={selectedItems}
                            t={t}
                            order={selectedOrder}
                            showItemStatus={showItemsOnKds}
                            onAdvanceItem={onAdvanceItem}
                            isItemDisabled={isItemDisabled}
                            getNextStatus={getNextStatus}
                            distanceLocale={distanceLocale}
                        />
                    </Box>
                </Stack>
            </CardContent>
            <CardActions
                sx={{
                    p: 2,
                    pt: 0,
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: "flex-end",
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{ fontWeight: 900 }}
                >
                    {t("kds.backToOrders")}
                </Button>
                {!selectedOrder?.food_ready_time ? (
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        startIcon={<CheckCircleOutline />}
                        onClick={() => onMarkReady(selectedOrder)}
                        disabled={isMarkReadyLoading}
                        sx={{ fontWeight: 900 }}
                    >
                        {t("kds.readyAll")}
                    </Button>
                ) : null}
            </CardActions>
        </Card>
    );
}
